import { FC, useState } from "react";
import { useSelector } from "react-redux";

import { ITooltipProps, Info, Tooltip } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import { newFeaturesCampaigns } from "constants/campaigns";
import { useNewFeatureForAgents } from "hooks/use-new-feature-for-agents";
import { selectCanChangeAutoassignment } from "store/entities/license";

interface ITooltipProperties {
  kind: ITooltipProps["kind"];
  markAsClosed: VoidFunction;
  width: number;
  header: string;
  text: string;
}

type IUseDisplayTooltip = ITooltipProperties | false;

const useDisplayTooltip = (): IUseDisplayTooltip => {
  // Dark mode promo tooltip
  const {
    isNewForAgent: isAgentCreatedBeforeTheDarkModeRelease,
    featureFlagValue: hasClosedDarkModePromoTooltip,
    setAgentFlagCallback: markDarkModePromoTooltipAsClosed,
  } = useNewFeatureForAgents(
    newFeaturesCampaigns.hasSeenDarkModePromoTooltip.flag,
    newFeaturesCampaigns.hasSeenDarkModePromoTooltip.campaignStartDate
  );
  const shouldDisplayDarkModePromoTooltip = !hasClosedDarkModePromoTooltip && isAgentCreatedBeforeTheDarkModeRelease;

  // Auto-assignment promo tooltip
  const {
    isNewForAgent: isAgentCreatedBeforeAgentAutoAssignmentManagementRelease,
    featureFlagValue: hasClosedProfileMenuAutoAssignmentTooltip,
    setAgentFlagCallback: markProfileMenuAutoAssignmentTooltipAsClosed,
  } = useNewFeatureForAgents(
    newFeaturesCampaigns.hasClosedProfileMenuAutoAssignmentTooltip.flag,
    newFeaturesCampaigns.hasClosedProfileMenuAutoAssignmentTooltip.campaignStartDate
  );
  const canChangeAutoassignment = useSelector(selectCanChangeAutoassignment);
  const shouldDisplayProfileMenuAutoAssignmentTooltip =
    isAgentCreatedBeforeAgentAutoAssignmentManagementRelease &&
    !hasClosedProfileMenuAutoAssignmentTooltip &&
    canChangeAutoassignment;

  if (shouldDisplayDarkModePromoTooltip) {
    return {
      kind: "important",
      markAsClosed: markDarkModePromoTooltipAsClosed,
      width: 200,
      header: "Ease your eyes with dark mode",
      text: "Switch to the new dark mode, ideal for low-light conditions.",
    };
  }

  if (shouldDisplayProfileMenuAutoAssignmentTooltip) {
    return {
      kind: "invert",
      markAsClosed: markProfileMenuAutoAssignmentTooltipAsClosed,
      width: 320,
      header: "Set up auto-assignment",
      text: "Decide if you want incoming tickets to be automatically assigned to you so that you can start resolving them right away.",
    };
  }

  return false;
};

interface IProps {
  triggerRenderer: React.ReactElement;
}

export const NavigationMyProfileTooltip: FC<IProps> = ({ triggerRenderer }) => {
  const displayTooltip = useDisplayTooltip();
  const [isVisible, setIsVisible] = useState(Boolean(displayTooltip));

  if (!displayTooltip) {
    return <>{triggerRenderer}</>;
  }

  const { kind, markAsClosed, width, header, text } = displayTooltip;

  const handleCloseTooltip = (): void => {
    setIsVisible(false);
    markAsClosed();
  };

  return (
    <Tooltip
      kind={kind}
      isVisible={isVisible}
      placement="right-end"
      triggerRenderer={<div onClick={handleCloseTooltip}>{triggerRenderer}</div>}
      offsetCrossAxis={-8}
    >
      <Block width={`${width}px`} maxWidth="100%">
        <Info closeWithX handleCloseAction={handleCloseTooltip} header={header} text={text} theme={kind} />
      </Block>
    </Tooltip>
  );
};
